// Standardised issue comments — source of truth is the pack supplied by Simon
// Gillies, "Standarised Comments updated - Karl 2 pages.pptx" (10 Aug 2026).
// Text is verbatim from that deck; do not reword without a new version of it.
//
// Reviewers pick one of these from a dropdown to save typing the same phrasing
// over and over. The free-text Comment box stays available alongside it for
// anything the preset list doesn't cover — that is a requirement of the deck,
// not an implementation detail.

// Category → comments, in deck order.
export const COMMENT_CATEGORIES = [
    {
        key: "donuts",
        label: "Donuts",
        comments: [
            "Please start defrosting donuts and fill trays. Thank you.",
        ],
    },
    {
        key: "pies",
        label: "Pies",
        comments: [
            "Please fill empty pie face trays. Thank you.",
            "Please ensure pies are properly positioned in their trays. Thank you.",
        ],
    },
    {
        key: "drink_impulse_fridge",
        label: "Drink / Impulse Fridge",
        comments: [
            "Please fill the gaps in the fridge. Thank you.",
        ],
    },
    {
        key: "pos_counter",
        label: "POS Counter",
        comments: [
            "Please ensure CFD is always turned on with promo display. Raise WO and indicate it in the comment if it's not working. Thank you.",
        ],
    },
    {
        key: "coffee",
        label: "Coffee",
        comments: [
            "Please ensure all cup sizes are restocked. Thank you.",
            "Please keep the coffee station neat at all times. Thank you.",
        ],
    },
    {
        key: "front_counter",
        label: "Front Counter",
        comments: [
            "Please ensure all shelf gaps are filled. Thank you.",
        ],
    },
    {
        key: "freezer",
        label: "Freezer",
        comments: [
            "Please restock products in the freezer or raise job log if it's not working and indicate in the comment. Thank you.",
        ],
    },
    {
        key: "promotions",
        label: "Promotions",
        comments: [
            "Ensure TV screen display is always turned on. Thank you.",
        ],
    },
    {
        key: "back_door",
        label: "Back Door",
        comments: [
            "Ensure the back door is always closed. Thank you.",
        ],
    },
    {
        key: "safety_sign",
        label: "Safety Sign",
        comments: [
            "Please remove the wet floor sign once the floor has dried. Thank you.",
        ],
    },
    {
        key: "gondola",
        label: "Gondola",
        comments: [
            "Please ensure all products are stocked and front-faced. Thank you.",
        ],
    },
    {
        key: "staff_conduct",
        label: "Staff Uniform & Conduct",
        comments: [
            "Please always wear your name badge. Thank you.",
            "Ensure staff member behind the counter are wearing the proper uniform. Thank you.",
            "Please use the chair only when doing admin tasks. Thank you.",
            "Please avoid using your phone while on duty unless work-related. Thank you.",
        ],
    },
    {
        key: "fresh_food",
        label: "Fresh Food",
        comments: [
            "Please restock fresh fruits. Thank you.",
        ],
    },
    {
        key: "macarons",
        label: "Macarons",
        comments: [
            "Please refill macaron and mochi balls that are low in stock. Thank you.",
        ],
    },
    {
        key: "other_zones",
        label: "Other Lower-Volume Zones",
        comments: [
            "Please restock ice cream cages. Thank you.",
        ],
    },
    {
        key: "everything_else",
        label: "Everything Else",
        comments: [
            "Please restock empty box and tickets with products and put comments once done. Thank you.",
        ],
    },
    {
        key: "pump",
        label: "Pump",
        comments: [
            "Please check the Out of Order sign and note the issue with the WO in the comments. Thank you.",
        ],
    },
    {
        key: "standalone_display",
        label: "Standalone Display",
        comments: [
            "Please ensure the standalone display is fully restocked. Thank you.",
        ],
    },
];

const CATEGORY_BY_KEY = COMMENT_CATEGORIES.reduce((acc, c) => {
    acc[c.key] = c;
    return acc;
}, {});

// Fixture type → the categories that belong to that fixture. Keys are the
// fixture_type vocabulary from src/api/routes/presets.js (FIXTURE_MAP /
// PREFIX_MAP) — keep them in step when a new fixture type is added there.
// Categories that apply to any fixture live in GENERAL_CATEGORY_KEYS below and
// are appended automatically, so don't repeat them here.
export const FIXTURE_COMMENT_CATEGORIES = {
    gondola:               ["gondola"],
    gondola_shelving:      ["gondola"],
    gondola_bay:           ["gondola"],
    endcap:                ["gondola"],
    auto_wall:             ["gondola"],
    confectionery_wall:    ["gondola", "front_counter"],
    confectionery_display: ["gondola", "front_counter"],
    counter_display:       ["front_counter"],
    counter_confectionery: ["front_counter"],
    counter_area:          ["front_counter", "pos_counter", "promotions"],
    fridge:                ["drink_impulse_fridge"],
    fridge_door:           ["drink_impulse_fridge"],
    fridge_wall:           ["drink_impulse_fridge"],
    refrigerated_drinks:   ["drink_impulse_fridge"],
    impulse_fridge:        ["drink_impulse_fridge"],
    standalone_fridge:     ["drink_impulse_fridge"],
    brunetti_fridge:       ["drink_impulse_fridge"],
    freezer:               ["freezer"],
    freezer_door:          ["freezer"],
    ice_cream_freezer:     ["freezer", "other_zones"],
    pie_cabinet:           ["pies", "donuts"],
    pie_face_cabinet:      ["pies", "donuts"],
    coffee_station:        ["coffee"],
    smoothie_station:      ["coffee"],
    pos_counter:           ["pos_counter", "front_counter", "promotions"],
    counter_register:      ["pos_counter", "front_counter", "promotions"],
    macaron_cabinet:       ["macarons"],
    standalone_display:    ["standalone_display"],
    magazine_rack:         ["standalone_display"],
    floor_stacks:          ["standalone_display"],
    entry_exit:            ["promotions"],
    drive_thru:            ["promotions"],
    forecourt:             ["pump"],
    store_overview:        ["promotions", "fresh_food"],
    store_floor:           ["promotions", "fresh_food"],
    fruit_stand:           ["fresh_food"],
    dining_seating:        [],
    // "unknown" and "unclassified" are deliberately absent — roughly a third of
    // SiteConfig rows carry them, and those reviewers get the full list rather
    // than a wrongly-narrowed one.
};

// Site-wide behaviours that can be observed in any fixture's photo — offered
// on every fixture in a second group under the fixture's own comments.
export const GENERAL_CATEGORY_KEYS = ["staff_conduct", "back_door", "safety_sign", "everything_else"];

const prettyFixture = (fixtureType) =>
    String(fixtureType || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Grouped standard comments for a fixture type, ready to render as <optgroup>s.
 *
 * Known fixture type  → its own categories first, then the general ones.
 * Unknown/absent type → every category, so nothing is unreachable (the dip
 *                       review form and other freeform callers land here).
 *
 * @param {string=} fixtureType e.g. "coffee_station"
 * @returns {Array<{label: string, comments: string[]}>}
 */
export function getStandardComments(fixtureType) {
    const mapped = FIXTURE_COMMENT_CATEGORIES[fixtureType];

    if (!mapped) {
        return COMMENT_CATEGORIES.map((c) => ({ label: c.label, comments: c.comments }));
    }

    const groups = [];
    const fixtureComments = mapped
        .map((k) => CATEGORY_BY_KEY[k])
        .filter(Boolean)
        .flatMap((c) => c.comments);

    if (fixtureComments.length > 0) {
        groups.push({ label: prettyFixture(fixtureType), comments: fixtureComments });
    }

    const generalComments = GENERAL_CATEGORY_KEYS
        .map((k) => CATEGORY_BY_KEY[k])
        .filter(Boolean)
        .flatMap((c) => c.comments);

    if (generalComments.length > 0) {
        groups.push({ label: "General — any fixture", comments: generalComments });
    }

    return groups;
}

export default getStandardComments;
